import { ConfigService } from './../config-service/config.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IPack } from '@stockcare-models/IPack';
import { Observable } from 'rxjs';
import { PerformanceMetricService } from '@stockcare-services/performance-metric-service/performance-metric-service';
import { AnalyticsNames } from '@stockcare-helpers/analytics-names';
import { PackStorageService } from '@stockcare-storage/pack/pack-storage.service';
import { Pack } from '@stockcare-storage/pack/pack';
import { IPackMaterial } from '@stockcare-models/IPackMaterials';
import { TheatreBasketPack } from '@stockcare-models/TheatreBasketPack';
import { TheatreBasketPackMaterial } from '@stockcare-models/TheatreBasketPackMaterial';
import { Stock } from '@stockcare-storage/stock/stock';
import { StockStorageService } from '@stockcare-storage/stock/stock-storage.service';
import { PackMaterial } from '@stockcare-storage/pack-material-service/pack-material';
import { TheatrePatientMaterials } from '@stockcare-storage/theatre-patient-materials/theatre-patient-materials';
import { take } from 'rxjs/operators';
import { TheatreBasketPackService } from '@stockcare-models/TheatreBasketPackService';
import { IPackService } from '@stockcare-models/IPackService';
import { TheatrePatientServices } from '@stockcare-storage/theatre-patient-services/theatre-patient-services';
import { ReportingParameters } from '@stockcare-models/ReportingParameters';
import { AuthenticationService } from '@stockcare-services/authentication/authentication.service';

@Injectable({
  providedIn: 'root',
})
export class PackService {
  constructor(
    private readonly http: HttpClient,
    private readonly configurationService: ConfigService,
    private readonly performanceMetricService: PerformanceMetricService,
    private readonly packStorageService: PackStorageService,
    private readonly stockStorageService: StockStorageService,
    private readonly authenticationService: AuthenticationService
  ) {}

  getPacks(institutionId: number, stockLocationId: number): Observable<IPack[]> {
    const startTime = new Date();
    return new Observable((observer) => {
      this.http
        .get<IPack[]>(
          `${this.configurationService.config.baseUrl}/StockManagement/packList/institution/${institutionId}/stockLocation/${stockLocationId}`
        )
        .subscribe(
          (result) => {
            this.performanceMetricService.buildAndSubmitMetrics(startTime, AnalyticsNames.packListQuery);
            this.packStorageService.deleteAll();
            this.packStorageService.addPacks(this.getPacksFromIPacks(result));
            observer.next(result);
          },
          () => {
            this.packStorageService.getPacks().then((result) => {
              observer.next(this.convertPackToIPack(result));
            });
          }
        );
    });
  }

  getListOfPacks(institutionId: number) {
    return this.http.get<IPack[]>(`${this.configurationService.config.baseUrl}/pack/institution/${institutionId}`);
  }

  getById(id: number) {
    return this.http.get<IPack>(`${this.configurationService.config.baseUrl}/Pack/getbyid/${id}`);
  }

  delete(pack: IPack) {
    return this.http.post<any[]>(`${this.configurationService.config.baseUrl}/Pack/delete`, { packId: pack.id });
  }

  update(pack: IPack) {
    return this.http.post<any[]>(`${this.configurationService.config.baseUrl}/Pack/edit`, {
      pack: pack,
    });
  }

  create(pack: IPack) {
    return this.http.post<any[]>(`${this.configurationService.config.baseUrl}/Pack/create`, {
      pack: pack,
    });
  }

  private getPacksFromIPacks(input: IPack[]): Pack[] {
    let packs: Pack[] = [];
    input.forEach((x) => {
      const pack = new Pack();
      pack.barcode = x.barcode;
      pack.id = x.id;
      pack.name = x.name;
      const packMaterials: PackMaterial[] = [];
      x.materials.forEach((y) => {
        packMaterials.push({
          barcode: y.barcode,
          id: y.id,
          quantity: y.quantity,
          unitOfMeasure: y.unitOfMeasure,
          packId: x.id,
          name: x.name,
        });
      });
      pack.packMaterials = packMaterials;
      packs.push(pack);
    });
    return packs;
  }

  convertPackToIPack(input: Pack[]): IPack[] {
    let packs: IPack[] = [];
    input.forEach((x) => {
      const packMaterials: IPackMaterial[] = [];
      const packServices: IPackService[] = [];
      x.packMaterials?.forEach((y) => {
        packMaterials.push({
          barcode: y.barcode,
          id: y.id,
          quantity: y.quantity,
          unitOfMeasure: y.unitOfMeasure,
          name: y.name,
        });
      });
      x.packServices?.forEach((s) => {
        packServices.push({
          barcode: s.barcode,
          serviceId: s.id,
          name: s.name,
        });
      });
      const pack: IPack = {
        name: x.name,
        id: x.id,
        dateAdded: new Date(),
        stockLocationId: 0,
        institutionId: 0,
        barcode: x.barcode,
        materials: packMaterials,
        services: packServices,
        isManagedBySourceOfRecords: true,
        stockLocationIds: [],
      };
      packs.push(pack);
    });
    return packs;
  }

  async buildBasketPack(pack: IPack): Promise<TheatreBasketPack> {
    const basketPack = new TheatreBasketPack();
    basketPack.id = pack.id;
    basketPack.name = pack.name;
    basketPack.barcode = pack.barcode;
    basketPack.materials = [];
    basketPack.services = [];

    await Promise.all(
      pack.materials.map(async (packMaterial) => {
        const basketPackMaterial = new TheatreBasketPackMaterial();
        basketPackMaterial.barcode = packMaterial.barcode;
        basketPackMaterial.packQuantity = packMaterial.quantity;
        const stockMaterial: Stock = await this.stockStorageService.getStockByIndex(packMaterial.barcode);
        if (stockMaterial) {
          basketPackMaterial.availableQuantity = packMaterial.availableQuantity;
          basketPackMaterial.unitOfMeasure = packMaterial.unitOfMeasure;
          basketPackMaterial.name = stockMaterial.name;
          if (packMaterial.quantity > packMaterial.availableQuantity) {
            basketPack.hasUnavailableItems = true;
            basketPackMaterial.unavailable = true;
          }
          basketPack.materials.push(basketPackMaterial);
        } else {
          basketPack.hasUnavailableItems = true;
          basketPackMaterial.unavailable = true;

          basketPackMaterial.availableQuantity = 0;
          basketPackMaterial.unitOfMeasure = packMaterial.unitOfMeasure;
          basketPackMaterial.name = packMaterial.name;

          basketPack.materials.push(basketPackMaterial);
        }
      })
    );

    await Promise.all(
      pack.services.map((service) => {
        const basketPackService = new TheatreBasketPackService();
        basketPackService.serviceId = service.serviceId;
        basketPackService.name = service.name;
        basketPackService.barcode = service.barcode;

        basketPack.services.push(basketPackService);
      })
    );

    return basketPack;
  }

  async buildPackDataMaterials(packs: TheatreBasketPack[], material: TheatrePatientMaterials) {
    const existingPack = packs.find((p) => p.id === material.PackId && p.packBasketId === material.PackBasketId);
    if (existingPack) {
      const existingMaterial = existingPack.materials.find((x) => x.barcode === material.Barcode);
      if (!existingMaterial) {
        const materialToAdd = new TheatreBasketPackMaterial();
        materialToAdd.barcode = material.Barcode;
        materialToAdd.unitOfMeasure = material.UnitOfMeasure;
        materialToAdd.packQuantity = material.Quantity;
        materialToAdd.packId = material.PackId;
        materialToAdd.name = material.Name;
        materialToAdd.packBasketId = material.PackBasketId;
        materialToAdd.materialId = material.MaterialId;
        materialToAdd.availableQuantity = material.Quantity;
        materialToAdd.userName = this.authenticationService.getUsername();
        existingPack.materials.push(materialToAdd);
      }
    } else {
      const pack = await this.packStorageService.getPackByIdIndex(material.PackId);
      if (!pack) return;

      const materialToAdd = new TheatreBasketPackMaterial();
      materialToAdd.barcode = material.Barcode;
      materialToAdd.name = material.Name;
      materialToAdd.unitOfMeasure = material.UnitOfMeasure;
      materialToAdd.packQuantity = material.Quantity;
      materialToAdd.packId = material.PackId;
      materialToAdd.materialId = material.MaterialId;
      materialToAdd.name = material.Name;
      materialToAdd.packBasketId = material.PackBasketId;
      materialToAdd.availableQuantity = material.Quantity;
      materialToAdd.userName = this.authenticationService.getUsername();
      const packToAdd = new TheatreBasketPack();
      packToAdd.barcode = pack.barcode;
      packToAdd.id = pack.id;
      packToAdd.name = pack.name;
      packToAdd.materials = [materialToAdd];
      packToAdd.services = [];
      packToAdd.hasUnavailableItems = false;
      packToAdd.packBasketId = material.PackBasketId;
      packs.unshift(packToAdd);
    }
  }

  async buildPackDataServices(packs: TheatreBasketPack[], service: TheatrePatientServices) {
    const existingPack = packs.find((p) => p.id === service.PackId && p.packBasketId === service.PackBasketId);
    if (existingPack) {
      const existingService = existingPack.services.find((x) => x.barcode === service.Barcode);
      if (!existingService) {
        const serviceToAdd = new TheatreBasketPackService();
        serviceToAdd.barcode = service.Barcode;
        serviceToAdd.packId = service.PackId;
        serviceToAdd.name = service.Name;
        serviceToAdd.packBasketId = service.PackBasketId;
        serviceToAdd.serviceId = service.Id;
        serviceToAdd.userName = this.authenticationService.getUsername();
        existingPack.services.push(serviceToAdd);
      }
    } else {
      const pack = await this.packStorageService.getPackByIdIndex(service.PackId);
      if (!pack) return;
      const serviceToAdd = new TheatreBasketPackService();
      serviceToAdd.barcode = service.Barcode;
      serviceToAdd.name = service.Name;
      serviceToAdd.packId = service.PackId;
      serviceToAdd.serviceId = service.Id;
      serviceToAdd.name = service.Name;
      serviceToAdd.packBasketId = service.PackBasketId;
      serviceToAdd.userName = this.authenticationService.getUsername();
      const packToAdd = new TheatreBasketPack();
      packToAdd.barcode = pack.barcode;
      packToAdd.id = pack.id;
      packToAdd.name = pack.name;
      packToAdd.services = [serviceToAdd];
      packToAdd.hasUnavailableItems = false;
      packToAdd.packBasketId = service.PackBasketId;
      packs.unshift(packToAdd);
    }
  }

  newGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  getFilteredPacks(parameters: ReportingParameters): Observable<IPack[]> {
    return this.http.post<IPack[]>(
      `${this.configurationService.config.baseUrl}/pack/GetFilteredPacks`,
      parameters
    );
  }
}
